import { useState, useEffect, useCallback, useMemo } from "react";
import { authFetch } from "@/lib/authFetch";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// FIN-7 — useFinanceConexoes.
//
// Estado da aba Conexões. Lê:
//   /api/finance/connections          → {enabled, items[], total_count}
//   /api/config/public                → flags (cache local 60s)
//
// Modo dormente: quando enabled=false, items vem [] e UI mostra hero +
// "como funciona" + empty state convidativo. Nenhuma chamada de
// initiate/refresh/disconnect é disparada.
//
// Modo ativo (futuro pré-lançamento, sem refactor de UI):
//   - initiate(): POST /api/finance/connections/initiate → connect_token
//   - finalize(payload): POST /api/finance/connections (callback Pluggy)
//   - refreshConnection(id): POST /api/finance/connections/{id}/refresh
//   - disconnectConnection(id): DELETE /api/finance/connections/{id}
//
// Polling: quando alguma conexão está em status "updating", repolla a
// lista a cada 8s. Caps em 6 polls (~48s) pra evitar loop infinito em
// caso de erro silencioso da Pluggy. Webhook normalmente chega antes.

const POLL_INTERVAL_MS = 8000;
const POLL_MAX_TICKS = 6;

async function _safeJson(url, init) {
  try {
    const r = await authFetch(url, init);
    const body = await r.json().catch(() => ({}));
    if (!r.ok) {
      return {
        ok: false,
        status: r.status,
        error: body?.detail?.message || body?.detail || `HTTP ${r.status}`,
        data: body,
      };
    }
    return { ok: true, status: r.status, error: null, data: body };
  } catch (e) {
    return { ok: false, status: 0, error: String(e?.message || e), data: null };
  }
}

export default function useFinanceConexoes() {
  const [enabled, setEnabled] = useState(false);
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pollTick, setPollTick] = useState(0);
  const [candidates, setCandidates] = useState([]);

  const fetchConnections = useCallback(async () => {
    const r = await _safeJson(`${API_URL}/api/finance/connections`);
    if (r.ok && r.data) {
      setEnabled(Boolean(r.data.enabled));
      setItems(Array.isArray(r.data.items) ? r.data.items : []);
      setError(null);
    } else {
      setError(r.error);
    }
    setIsLoading(false);
    return r;
  }, []);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  // Polling ligado quando alguma conexão está atualizando.
  const hasUpdating = useMemo(
    () => items.some((c) => c.status === "updating"),
    [items],
  );

  useEffect(() => {
    if (!enabled || !hasUpdating || pollTick >= POLL_MAX_TICKS) return;
    const t = setTimeout(() => {
      fetchConnections();
      setPollTick((p) => p + 1);
    }, POLL_INTERVAL_MS);
    return () => clearTimeout(t);
  }, [enabled, hasUpdating, pollTick, fetchConnections]);

  // Reset do tick quando lista deixa de ter "updating"
  useEffect(() => {
    if (!hasUpdating && pollTick !== 0) setPollTick(0);
  }, [hasUpdating, pollTick]);

  const initiate = useCallback(async () => {
    if (!enabled) {
      throw new Error("open_finance_disabled");
    }
    const r = await _safeJson(
      `${API_URL}/api/finance/connections/initiate`,
      { method: "POST" },
    );
    if (!r.ok) throw new Error(r.error || "initiate_failed");
    return r.data; // { connect_token }
  }, [enabled]);

  const finalize = useCallback(async (payload) => {
    if (!enabled) {
      throw new Error("open_finance_disabled");
    }
    const r = await _safeJson(`${API_URL}/api/finance/connections`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!r.ok) throw new Error(r.error || "finalize_failed");
    await fetchConnections();
    return r.data;
  }, [enabled, fetchConnections]);

  const refreshConnection = useCallback(async (connectionId) => {
    if (!enabled) {
      throw new Error("open_finance_disabled");
    }
    // Optimistic: status local vira "updating" antes do round-trip.
    setItems((prev) =>
      prev.map((c) => (c.id === connectionId ? { ...c, status: "updating" } : c)),
    );
    const r = await _safeJson(
      `${API_URL}/api/finance/connections/${connectionId}/refresh`,
      { method: "POST" },
    );
    if (!r.ok) {
      await fetchConnections(); // reverte estado
      throw new Error(r.error || "refresh_failed");
    }
    setPollTick(0); // re-arma polling pra ver status final
    return r.data;
  }, [enabled, fetchConnections]);

  // ── Reconciliation candidates (H0-S3) ──────────────────────────
  const fetchCandidates = useCallback(async (connectionId) => {
    if (!enabled || !connectionId) {
      setCandidates([]);
      return [];
    }
    const r = await _safeJson(
      `${API_URL}/api/finance/connections/${connectionId}/reconciliation_candidates`,
    );
    if (r.ok && r.data) {
      const list = Array.isArray(r.data.candidates) ? r.data.candidates : [];
      setCandidates(list);
      return list;
    }
    setCandidates([]);
    return [];
  }, [enabled]);

  const resolveCandidate = useCallback(
    async (connectionId, candidateId, decision) => {
      if (!enabled) {
        throw new Error("open_finance_disabled");
      }
      const r = await _safeJson(
        `${API_URL}/api/finance/connections/${connectionId}/reconcile/${candidateId}?decision=${decision}`,
        { method: "POST" },
      );
      if (!r.ok) throw new Error(r.error || "resolve_failed");
      // Remove o candidato da lista local
      setCandidates((prev) => prev.filter((c) => c.id !== candidateId));
      return r.data;
    },
    [enabled],
  );

  const disconnectConnection = useCallback(async (connectionId) => {
    if (!enabled) {
      throw new Error("open_finance_disabled");
    }
    const r = await _safeJson(
      `${API_URL}/api/finance/connections/${connectionId}`,
      { method: "DELETE" },
    );
    if (!r.ok) throw new Error(r.error || "disconnect_failed");
    setItems((prev) => prev.filter((c) => c.id !== connectionId));
    return r.data;
  }, [enabled]);

  // Métricas de cabeçalho pra UI.
  const stats = useMemo(() => {
    const total = items.length;
    const updated = items.filter((c) => c.status === "updated").length;
    const errored = items.filter((c) =>
      ["error", "login_required"].includes(c.status),
    ).length;
    const updating = items.filter((c) => c.status === "updating").length;
    return { total, updated, errored, updating };
  }, [items]);

  return {
    enabled,
    items,
    stats,
    isLoading,
    error,
    refetch: fetchConnections,
    initiate,
    finalize,
    refreshConnection,
    disconnectConnection,
    candidates,
    fetchCandidates,
    resolveCandidate,
  };
}
